import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { SERVICE_TYPE_ERROR_MESSAGES } from '../errors';
import type { ServiceType } from '../types/serviceTypeTypes';
import serviceTypeEndpoints from './serviceTypeEndpoints';

export interface EditServiceTypeInput {
  serviceName: string;
  description: string;
}

export interface EditServiceTypeResult {
  success: boolean;
  data?: ServiceType;
  error?: string;
  fieldErrors?: Record<string, string>;
}

interface ApiErrorData {
  message?: string;
  error?: string;
  statusCode?: number;
  code?: string;
  details?: Array<{ path?: string; message?: string; code?: string }>;
}

const getMessageFromPayload = (errorData: unknown): string | null => {
  if (!errorData) {
    return null;
  }
  if (typeof errorData === 'string') {
    return errorData;
  }
  if (typeof errorData !== 'object') {
    return null;
  }
  const obj = errorData as ApiErrorData;
  return obj.message || obj.error || null;
};

const getFieldErrorsFromPayload = (
  errorData: unknown
): Record<string, string> | undefined => {
  if (!errorData || typeof errorData !== 'object') {
    return undefined;
  }
  const obj = errorData as ApiErrorData;
  const details = obj.details;
  if (!Array.isArray(details) || details.length === 0) {
    return undefined;
  }
  const fieldErrors: Record<string, string> = {};
  for (const d of details) {
    if (d?.path && d?.message) {
      fieldErrors[d.path] = d.message;
    }
  }
  return Object.keys(fieldErrors).length > 0 ? fieldErrors : undefined;
};

const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 401 || status === 403) {
    return SERVICE_TYPE_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 409) {
    return SERVICE_TYPE_ERROR_MESSAGES.NAME_ALREADY_EXISTS;
  }
  if (status === 500) {
    return SERVICE_TYPE_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (status === 400) {
    const fieldErrors = getFieldErrorsFromPayload(errorData);
    const firstDetailMessage =
      fieldErrors && Object.keys(fieldErrors).length > 0
        ? Object.values(fieldErrors)[0]
        : null;
    return (
      firstDetailMessage ||
      getMessageFromPayload(errorData) ||
      SERVICE_TYPE_ERROR_MESSAGES.VALIDATION_ERROR
    );
  }
  return (
    getMessageFromPayload(errorData) ??
    SERVICE_TYPE_ERROR_MESSAGES.DEFAULT_ERROR
  );
};

export const editServiceType = async (
  serviceTypeId: string,
  input: EditServiceTypeInput
): Promise<EditServiceTypeResult> => {
  const trimmedId = serviceTypeId?.trim();
  if (!trimmedId) {
    return {
      success: false,
      error: SERVICE_TYPE_ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const payload = {
      service_name: input.serviceName.trim(),
      description: input.description.trim(),
    };

    const result = await apiService.patchAPI(
      `${serviceTypeEndpoints.SERVICE_TYPES}/${trimmedId}`,
      payload
    );

    if (!result.error) {
      const raw = (result.data ?? {}) as {
        service_type?: ServiceType;
        data?: ServiceType;
      } & Partial<ServiceType>;
      const data = (raw.service_type ?? raw.data ?? raw) as ServiceType;
      return { success: true, data };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: SERVICE_TYPE_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    const errorMessage = extractErrorMessage(result.data, result.status);
    let fieldErrors = getFieldErrorsFromPayload(result.data);
    if (result.status === 409) {
      fieldErrors = {
        ...(fieldErrors ?? {}),
        service_name: SERVICE_TYPE_ERROR_MESSAGES.NAME_ALREADY_EXISTS,
      };
    }

    return {
      success: false,
      error: errorMessage,
      ...(fieldErrors && { fieldErrors }),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const status = error.response?.status;
      const errorData = error.response?.data;
      const isNetworkError = !error.response && !!error.request;

      if (isNetworkError) {
        return {
          success: false,
          error: SERVICE_TYPE_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }

      const errorMessage = extractErrorMessage(errorData, status);
      let fieldErrors = getFieldErrorsFromPayload(errorData);
      if (status === 409) {
        fieldErrors = {
          ...(fieldErrors ?? {}),
          service_name: SERVICE_TYPE_ERROR_MESSAGES.NAME_ALREADY_EXISTS,
        };
      }
      return {
        success: false,
        error: errorMessage,
        ...(fieldErrors && { fieldErrors }),
      };
    }

    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : SERVICE_TYPE_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
